import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, CheckCircle, XCircle, Clock, Edit, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDate } from '@/utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const OrderRequest = () => {
  const [requests, setRequests] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [open, setOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user?.role;
  const isCustomer = userRole === 'customer';
  const isDistributor = userRole === 'distributor';
  const isAdmin = userRole === 'admin';
  const isManager = userRole === 'manager';

  const [formData, setFormData] = useState({
    distributor_id: '',
    distributor_name: '',
    large_eggs: '',
    medium_eggs: '',
    small_eggs: '',
    cracked_eggs: '',
    delivery_date: '',
    delivery_address: '',
    notes: ''
  });

  useEffect(() => {
    fetchRequests();
    if (isCustomer) {
      fetchDistributors();
    }
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/order-requests`, getAuthHeader());
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch order requests');
    }
  };

  const fetchDistributors = async () => {
    try {
      const response = await axios.get(`${API}/distributors/list`, getAuthHeader());
      setDistributors(response.data);
    } catch (error) {
      console.error('Failed to fetch distributors');
    }
  };

  const handleDistributorChange = (distributorId) => {
    const distributor = distributors.find(d => d.id === distributorId);
    if (distributor) {
      setFormData({
        ...formData,
        distributor_id: distributorId,
        distributor_name: distributor.name || distributor.username
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.distributor_id) {
      toast.error('Please select a distributor for your order');
      return;
    }
    
    try {
      await axios.post(`${API}/order-requests`, {
        ...formData,
        large_eggs: parseInt(formData.large_eggs) || 0,
        medium_eggs: parseInt(formData.medium_eggs) || 0,
        small_eggs: parseInt(formData.small_eggs) || 0,
        cracked_eggs: parseInt(formData.cracked_eggs) || 0
      }, getAuthHeader());
      toast.success('Order request submitted successfully');
      fetchRequests();
      setOpen(false);
      setFormData({
        distributor_id: '',
        distributor_name: '',
        large_eggs: '',
        medium_eggs: '',
        small_eggs: '',
        cracked_eggs: '',
        delivery_date: '',
        delivery_address: '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleStatusUpdate = async (requestId, status) => {
    try {
      await axios.put(`${API}/order-requests/${requestId}/status?status=${status}`, {}, getAuthHeader());
      toast.success(`Order ${status}`);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Delete this order request?')) return;
    try {
      await axios.delete(`${API}/order-requests/${requestId}`, getAuthHeader());
      toast.success('Order request deleted');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      rejected: 'bg-red-100 text-red-700',
      fulfilled: 'bg-emerald-100 text-emerald-700'
    };
    return badges[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
      case 'fulfilled':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  // Get page title based on role
  const getPageTitle = () => {
    if (isDistributor) return 'Customer Orders Received';
    return 'Customer Order Request';
  };

  const getPageDescription = () => {
    if (isCustomer) return 'Submit your egg order requests to a distributor';
    if (isDistributor) return 'View and fulfill customer order requests assigned to you';
    return 'Manage customer order requests';
  };

  return (
    <div className="space-y-6" data-testid="order-request-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {getPageTitle()}
          </h1>
          <p className="text-lg text-slate-600 mt-2">
            {getPageDescription()}
          </p>
        </div>
        {isCustomer && (
          <Button data-testid="new-request-button" onClick={() => setOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            New Order Request
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4 bg-gradient-to-br from-yellow-50 to-white border-l-4 border-yellow-500">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Pending</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">
            {requests.filter(r => r.status === 'pending').length}
          </p>
        </div>
        <div className="card p-4 bg-gradient-to-br from-blue-50 to-white border-l-4 border-blue-500">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Approved</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">
            {requests.filter(r => r.status === 'approved').length}
          </p>
        </div>
        <div className="card p-4 bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-500">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Fulfilled</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">
            {requests.filter(r => r.status === 'fulfilled').length}
          </p>
        </div>
        <div className="card p-4 bg-gradient-to-br from-red-50 to-white border-l-4 border-red-500">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Rejected</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">
            {requests.filter(r => r.status === 'rejected').length}
          </p>
        </div>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Request #</th>
                {!isCustomer && <th>Customer</th>}
                <th>Distributor</th>
                <th className="text-center">X-Large</th>
                <th className="text-center">Large</th>
                <th className="text-center">Medium</th>
                <th className="text-center">Small</th>
                <th className="text-center">Cracks</th>
                <th>Expected Pick-up Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody data-testid="requests-table-body">
              {requests.map((request) => (
                <tr key={request.id}>
                  <td className="font-mono font-semibold text-blue-600">{request.request_number || '-'}</td>
                  {!isCustomer && (
                    <td>
                      <div>
                        <p className="font-medium">{request.customer_name}</p>
                        <p className="text-xs text-slate-500">{request.customer_email}</p>
                      </div>
                    </td>
                  )}
                  <td className="text-purple-600 font-medium">
                    {request.distributor_name || <span className="text-slate-400">Direct to Farm</span>}
                  </td>
                  <td className="text-center">{request.xlarge_crates || 0}</td>
                  <td className="text-center">{request.large_crates || request.large_eggs || 0}</td>
                  <td className="text-center">{request.medium_crates || request.medium_eggs || 0}</td>
                  <td className="text-center">{request.small_crates || request.small_eggs || 0}</td>
                  <td className="text-center">{request.cracks_crates || request.cracked_eggs || 0}</td>
                  <td>{formatDate(request.expected_pickup_date || request.delivery_date)}</td>
                  <td>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${getStatusBadge(request.status)}`}>
                      {getStatusIcon(request.status)}
                      {request.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      {/* Admin/Manager/Distributor approval actions */}
                      {(isAdmin || isManager || isDistributor) && request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleStatusUpdate(request.id, 'approved')}
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                            title="Approve"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleStatusUpdate(request.id, 'rejected')}
                            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Reject"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {(isAdmin || isManager) && request.status === 'approved' && (
                        <button
                          onClick={() => handleStatusUpdate(request.id, 'fulfilled')}
                          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                          title="Mark Fulfilled"
                        >
                          <Package className="w-4 h-4" />
                        </button>
                      )}
                      
                      {/* Distributor actions - can mark approved orders as fulfilled */}
                      {isDistributor && request.status === 'approved' && (
                        <button
                          onClick={() => handleStatusUpdate(request.id, 'fulfilled')}
                          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                          title="Mark as Fulfilled"
                          data-testid={`fulfill-btn-${request.id}`}
                        >
                          <Package className="w-4 h-4" />
                        </button>
                      )}
                      {isDistributor && request.status === 'pending' && (
                        <span className="text-xs text-amber-600 px-2 py-1">Awaiting approval</span>
                      )}
                      
                      {/* Delete action - Admin/Manager can delete, customers can delete their own pending */}
                      {((isAdmin || isManager) || (isCustomer && request.status === 'pending')) && (
                        <button
                          onClick={() => handleDelete(request.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {requests.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              {isCustomer 
                ? 'You have no order requests yet. Click "New Order Request" to create one.' 
                : isDistributor 
                  ? 'No order requests assigned to you yet.'
                  : 'No order requests received.'}
            </div>
          )}
        </div>
      </div>

      {/* New Request Dialog - Only for Customers */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="request-dialog">
          <DialogHeader>
            <DialogTitle>New Order Request</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Distributor Selection - Required */}
            <div>
              <Label htmlFor="distributor">Select Distributor *</Label>
              <Select onValueChange={handleDistributorChange} value={formData.distributor_id}>
                <SelectTrigger data-testid="distributor-select" className="w-full">
                  <SelectValue placeholder="Choose a distributor for your order" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {distributors.map((distributor) => (
                    <SelectItem key={distributor.id} value={distributor.id}>
                      {distributor.name || distributor.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Your order will be sent to this distributor</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="large-eggs">Large Eggs</Label>
                <Input
                  id="large-eggs"
                  type="number"
                  data-testid="large-eggs-input"
                  value={formData.large_eggs}
                  onChange={(e) => setFormData({ ...formData, large_eggs: e.target.value })}
                  min="0"
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="medium-eggs">Medium Eggs</Label>
                <Input
                  id="medium-eggs"
                  type="number"
                  data-testid="medium-eggs-input"
                  value={formData.medium_eggs}
                  onChange={(e) => setFormData({ ...formData, medium_eggs: e.target.value })}
                  min="0"
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="small-eggs">Small Eggs</Label>
                <Input
                  id="small-eggs"
                  type="number"
                  data-testid="small-eggs-input"
                  value={formData.small_eggs}
                  onChange={(e) => setFormData({ ...formData, small_eggs: e.target.value })}
                  min="0"
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="cracked-eggs">Cracked Eggs</Label>
                <Input
                  id="cracked-eggs"
                  type="number"
                  data-testid="cracked-eggs-input"
                  value={formData.cracked_eggs}
                  onChange={(e) => setFormData({ ...formData, cracked_eggs: e.target.value })}
                  min="0"
                  placeholder="0"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="delivery-date">Preferred Delivery Date *</Label>
              <Input
                id="delivery-date"
                type="date"
                data-testid="delivery-date-input"
                value={formData.delivery_date}
                onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="delivery-address">Delivery Address</Label>
              <Input
                id="delivery-address"
                data-testid="delivery-address-input"
                value={formData.delivery_address}
                onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                placeholder="Enter delivery address"
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                data-testid="notes-input"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any special instructions..."
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" data-testid="submit-request-button" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                Submit Request
              </Button>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderRequest;
